import React from 'react';
import { Download } from 'lucide-react';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { formatMoney } from '../../utils/formatMoney';

const providerLabel = (value) => {
    if (!value) return '';
    const n = String(value).toLowerCase();
    if (n.includes('stripe')) return 'Stripe';
    if (n.includes('razorpay')) return 'Razorpay';
    if (n.includes('payu')) return 'PayU';
    if (n.includes('dummy')) return 'Manual';
    return String(value).replace(/_/g, ' ');
};

const InvoiceTable = ({ invoices, onDownload, onDownloadVendorInvoice, onApprove, onReject, onUpdateStatus }) => {
    const getStatusBadgeClass = (status) => {
        const statusClasses = {
            'draft': 'bg-gray-100 text-gray-800',
            'submitted': 'bg-yellow-100 text-yellow-800',
            'approved': 'bg-green-100 text-green-800',
            'rejected': 'bg-red-100 text-red-800',
            'paid': 'bg-blue-100 text-blue-800',
        };
        return statusClasses[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    const getTypeBadgeClass = (type) => {
        const typeClasses = {
            'service': 'bg-purple-100 text-purple-800',
            'miscellaneous': 'bg-orange-100 text-orange-800',
        };
        return typeClasses[type?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    const getPaymentStatusBadgeClass = (status) => {
        const statusClasses = {
            'processing': 'bg-yellow-100 text-yellow-800',
            'pending': 'bg-yellow-100 text-yellow-800',
            'completed': 'bg-green-100 text-green-800',
            'succeeded': 'bg-green-100 text-green-800',
            'failed': 'bg-red-100 text-red-800',
            'refunded': 'bg-gray-100 text-gray-700',
        };
        return statusClasses[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
    };

    const formatAmount = (amount) => {
        if (!amount) return formatMoney(0);
        return formatMoney(amount);
    };

    const getCustomerPayment = (invoice) => {
        const pay = invoice.customer_payment;
        const paymentId = pay?.provider_payment_id || invoice.payment_reference || null;
        const provider = pay?.provider || invoice.payment_method || null;
        let status = pay?.status || null;
        if (!status && (invoice.paid_at || String(invoice.status || '').toLowerCase() === 'paid')) {
            status = 'succeeded';
        }
        const paidAt = pay?.succeeded_at || invoice.paid_at || pay?.refunded_at || null;
        return {
            paymentId,
            provider,
            status,
            createdAt: pay?.created_at || null,
            paidAt,
            failureReason: pay?.failure_reason || null,
        };
    };

    return (
        <div className="overflow-x-auto border rounded-md">
            <table className="w-full">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Invoice #
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Type
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Vendor
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Status
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Total Amount
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Inv Created
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Payment ID
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Payment Status
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Paid At
                        </th>
                        <th className="px-6 py-4 text-left whitespace-nowrap text-1x1 font-medium text-black-500">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {invoices.length === 0 ? (
                        <tr>
                            <td colSpan="10" className="px-6 py-4 text-center text-gray-400">
                                No invoices found
                            </td>
                        </tr>
                    ) : (
                        invoices.map((invoice) => {
                            const payment = getCustomerPayment(invoice);
                            return (
                                <tr key={invoice.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1  border-t text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <span>{invoice.invoice_number}</span>
                                            {invoice.vendor_invoice_document_id && (
                                                <button
                                                    onClick={() => onDownloadVendorInvoice(invoice)}
                                                    className="text-green-600 hover:text-green-800"
                                                    title="Download Vendor Invoice"
                                                >
                                                    <Download className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 border-t">
                                        <span className={`px-2 py-1 text-xs rounded bg-red-300 text-white  ${getTypeBadgeClass(invoice.invoice_type)}`}>
                                            {invoice.invoice_type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 text-gray-600 border-t">
                                        {invoice.vendor_name}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 border-t">
                                        <span className={`px-2 py-1 text-xs rounded bg-sky-200 text-blue capitalize ${getStatusBadgeClass(invoice.status)}`}>
                                            {invoice.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 text-gray-600 border-t">
                                        {formatAmount(invoice.total_amount)}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 text-gray-600 border-t">
                                        {formatDate(invoice.created_at)}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 text-gray-600 border-t">
                                        {payment.paymentId ? (
                                            <div className="flex flex-col gap-0.5">
                                                <span className="font-mono text-xs" title={payment.paymentId}>
                                                    {payment.paymentId}
                                                </span>
                                                {payment.provider && (
                                                    <span className="text-xs text-gray-500 capitalize">
                                                        {providerLabel(payment.provider)}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 border-t">
                                        {payment.status ? (
                                            <div className="flex flex-col gap-1">
                                                <span className={`px-2 py-1 text-xs rounded capitalize ${getPaymentStatusBadgeClass(payment.status)}`}>
                                                    {payment.status}
                                                </span>
                                                {payment.failureReason && (
                                                    <span className="text-xs text-red-600" title={payment.failureReason}>
                                                        {payment.failureReason.length > 30
                                                            ? `${payment.failureReason.substring(0, 30)}...`
                                                            : payment.failureReason}
                                                    </span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 text-gray-600 border-t">
                                        {payment.paidAt ? formatDateTime(payment.paidAt) : (payment.createdAt ? formatDateTime(payment.createdAt) : '-')}
                                    </td>
                                    <td className="px-6 py-3 whitespace-nowrap text-1x1 font-medium border-t">
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => onDownload(invoice)}
                                                className="text-blue-600 hover:text-blue-800"
                                                title="Download Invoice"
                                            >
                                                <Download className="w-4 h-4" />
                                            </button>
                                            {(invoice.status === 'submitted' || invoice.status === 'approved') && (
                                                <button
                                                    onClick={() => onUpdateStatus(invoice)}
                                                    className="px-3 py-2 text-sm bg-scarlet-600 text-white rounded hover:bg-blue-700 transition-colors"
                                                    title="Update Status"
                                                >
                                                    Update Status
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default InvoiceTable;
